<template>
  <c-box>
    <c-box
      @click="openFileUpload"
      @drop="
        $event.preventDefault();
        $event.stopPropagation();
        handleDrop($event);
      "
      @dragover="
        $event.preventDefault();
        $event.stopPropagation();
      "
    >
      <input
        id="file"
        ref="file"
        type="file"
        style="display: none"
        :multiple="true"
        :accept="acceptedFileTypes.join(',')"
        @change="handleFileUpload()"
      />

      <slot
        name="fileUploadArea"
        :openFileUpload="openFileUpload"
        :attachments="attachments"
        :removeFile="removeFile"
      >
        <c-flex
          p="md"
          text-align="center"
          min-height="80px"
          bg="brand.grayLight"
          justify="center"
          align="center"
          border-radius="sharper"
          border-width="lg"
          border-style="dashed"
          :_hover="{
            borderColor: 'brand.grayDark',
            bg: 'white',
            cursor: 'pointer',
          }"
          class="form-group"
        >
          {{
            $t("support.dragAndDropTargetMessage", {
              count: maxFileCount,
              maxSize: maxTotalSize / 1000 / 1000,
            })
          }}
        </c-flex>
      </slot>
    </c-box>
    <c-box v-if="attachments.length">
      <slot name="fileListing" :attachments="attachments" :removeFile="removeFile">
        <c-flex wrap="wrap">
          <c-flex
            v-for="(fileUpload, index) in attachments"
            :key="fileUpload.name"
            align="center"
            justify="space-between"
            font-size="sm"
            height="26px"
            border-radius="input"
            background="brand.blueLightest"
            border-color="brand.grayDark"
            border-width="sm"
            pl="md"
            pr="2xs"
            mr="xs"
            mt="xs"
            font-weight="semibold"
          >
            {{ fileUpload.name }}
            <c-flex
              align="center"
              justify="center"
              color="black"
              margin-left="md"
              padding="2xs"
              :_hover="{
                color: 'brand.blueLight',
                cursor: 'pointer',
              }"
              @click="() => removeFile(index)"
            >
              <c-icon name="cross" size="1.25rem" />
            </c-flex>
          </c-flex>
        </c-flex>
      </slot>
    </c-box>
    <c-text v-if="errorMessage" as="span" d="block !important" class="invalid-feedback">
      {{ errorMessage }}
    </c-text>
  </c-box>
</template>

<script lang="ts">
import { CBox, CFlex, CIcon, CText } from "@chakra-ui/vue";
import { defineComponent } from "vue";

export default defineComponent({
  name: "FileUpload",
  components: {
    CBox,
    CFlex,
    CIcon,
    CText,
  },
  props: {
    maxTotalSize: {
      type: Number,
      default: 1.6e7,
    },
    maxFileCount: {
      type: Number,
      default: 5,
    },
    getAttachments: Function,
  },
  data() {
    return {
      attachments: [] as File[],
      errorMessage: "",
      timer: null as any,
      acceptedFileTypes: [
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/pdf",
        "application/doc",
        "image/png",
        "image/jpeg",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
      ],
    };
  },
  watch: {
    errorMessage() {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }
      if (this.errorMessage) {
        this.timer = setTimeout(() => {
          this.errorMessage = "";
        }, 5000);
      }
    },
  },
  beforeDestroy() {
    if (this.timer) window.clearTimeout(this.timer);
  },
  methods: {
    handleDrop({ dataTransfer }: { dataTransfer: DataTransfer }) {
      const { files } = dataTransfer;
      this.handleFileUpload(files);
    },
    openFileUpload() {
      (this.$refs.file as any)?.click();
    },
    checkAttachmentTotalSize(attachments: File[]) {
      const totalSize = attachments.reduce((acc, curr) => {
        return acc + curr.size;
      }, 0);

      if (totalSize > this.maxTotalSize) {
        this.errorMessage = this.$t("support.validations.tooBigFile", {
          maxSize: this.maxTotalSize / 1000 / 1000,
        }) as string;
        return false;
      }
      return true;
    },
    checkAttachmentCount(attachments: File[]) {
      const attachmentCount = attachments.length;
      if (attachmentCount < this.maxFileCount) return true;

      this.errorMessage = this.$t("support.validations.tooManyFiles", {
        count: this.maxFileCount,
      }) as string;
      return false;
    },
    filterFoldersAndUnsupportedFileTypes(files: FileList) {
      return [...files].filter((file) => this.acceptedFileTypes.includes(file.type));
    },
    handleFileUpload(_files?: FileList) {
      if (_files) {
        const files = this.filterFoldersAndUnsupportedFileTypes(_files);
        if (this.checkAttachmentTotalSize([...files])) {
          files.forEach((file) => {
            if (this.checkAttachmentCount(this.attachments))
              this.attachments = this.attachments.concat(file);
          });
        }
      } else if ((this.$refs.file as any)?.files) {
        const { files: _files } = this.$refs.file as any;
        if (_files) {
          const files = this.filterFoldersAndUnsupportedFileTypes(_files);
          if (files && this.checkAttachmentTotalSize([...files])) {
            files.forEach((file) => {
              if (this.checkAttachmentCount(this.attachments))
                this.attachments = this.attachments.concat(file);
            });
          }
        }
      }
    },
    removeFile(index: number) {
      this.attachments = this.attachments.filter((file, arrayIndex) => {
        return index !== arrayIndex;
      });
    },
  },
  updated() {
    if (this.getAttachments) this.getAttachments(this.attachments);
  },
});
</script>
